import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { Filial } from "@/types/data";
import { MailIcon, MapPin, PhoneIcon } from "lucide-react";

interface LocationCardProps {
	unidade: Filial;
	isHeadquarters?: boolean;
}

export function LocationCard({
	unidade,
	isHeadquarters = false,
}: LocationCardProps) {
	const { nome, endereco, email, telefones } = unidade;

	const fullAddress = [
		endereco.logradouro,
		endereco.numero,
		endereco.complemento,
		endereco.bairro,
		`${endereco.cidade}/${endereco.estado}`,
		endereco.cep,
	]
		.filter(Boolean)
		.join(", ");

	const hasMap = Boolean(
		endereco.google_maps_embed?.ativo && endereco.google_maps_embed.url,
	);

	return (
		<Card className="h-full overflow-hidden rounded-2xl border-zinc-200 py-0 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-zinc-300 hover:shadow-lg">
			<CardHeader className="border-b border-zinc-100 py-5">
				<CardTitle className="flex items-center gap-2 text-balance text-zinc-900">
					<MapPin className="h-5 w-5 shrink-0 text-brand-600" />
					<span className="flex-1">{nome}</span>
					{isHeadquarters && (
						<span className="rounded-full bg-brand-600 px-3 py-1 text-xs font-semibold text-white">
							Matriz
						</span>
					)}
				</CardTitle>
			</CardHeader>

			<CardContent className="flex flex-1 flex-col gap-4 py-5">
				<p className="text-sm leading-relaxed text-zinc-600">{fullAddress}</p>

				<div className="space-y-3">
					{telefones && telefones.length > 0 && (
						<div className="flex items-start gap-2 text-sm text-zinc-700">
							<PhoneIcon className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" />
							<div className="flex flex-col gap-1">
								{telefones.map((phone, index) => (
									<a
										key={`${phone.numero}-${index}`}
										href={`tel:${phone.numero.replace(/\D/g, "")}`}
										className="font-medium transition-colors hover:text-brand-700"
									>
										{phone.numero}
										{phone.descricao && (
											<span className="ml-1 font-normal text-zinc-500">
												({phone.descricao})
											</span>
										)}
									</a>
								))}
							</div>
						</div>
					)}

					{email && (
						<div className="flex items-start gap-2 text-sm text-zinc-700">
							<MailIcon className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" />
							<a
								href={`mailto:${email}`}
								className="break-all font-medium transition-colors hover:text-brand-700"
							>
								{email}
							</a>
						</div>
					)}
				</div>

				{hasMap && (
					<div className="mt-auto overflow-hidden rounded-xl border border-zinc-200 bg-zinc-50 shadow-sm">
						<iframe
							src={endereco.google_maps_embed.url}
							width="100%"
							height="250"
							style={{ border: 0 }}
							allowFullScreen
							loading="lazy"
							referrerPolicy="no-referrer-when-downgrade"
							title={`Mapa de ${nome}`}
						/>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
